import { useMemo, useState } from 'react'
import type { ChangeEvent, ReactElement } from 'react'

import type { WebhookBlock, WebhookIngressRequest } from '@/api/types'
import { Avatar, Button, Select, TextArea, TextField, useToast } from '@/components/ui'
import { WebhookBlocks } from '@/components/WebhookBlocks'
import styles from './WebhookMessageBuilder.module.css'

type BuilderMode = 'basic' | 'blocks'
type BlockType = WebhookBlock['type']

interface DraftBase {
  id: string
}

interface TitleDraft extends DraftBase {
  type: 'title'
  text: string
}

interface TextDraft extends DraftBase {
  type: 'text'
  text: string
}

interface ImageDraft extends DraftBase {
  type: 'image'
  url: string
  alt: string
}

interface TableDraft extends DraftBase {
  type: 'table'
  columns: string[]
  rows: string[][]
}

interface ActionsDraft extends DraftBase {
  type: 'actions'
  buttons: Array<{ text: string; url: string }>
}

type BlockDraft = TitleDraft | TextDraft | ImageDraft | TableDraft | ActionsDraft

const BLOCK_LIMIT = 20
const TABLE_ROW_LIMIT = 50
let draftSequence = 0

function draftId(): string {
  draftSequence += 1
  return `webhook-block-${draftSequence}`
}

function newDraft(type: BlockType): BlockDraft {
  switch (type) {
    case 'title':
      return { id: draftId(), type, text: 'Deployment complete' }
    case 'text':
      return { id: draftId(), type, text: 'Build **412** passed all checks.' }
    case 'image':
      return {
        id: draftId(),
        type,
        url: 'https://example.com/deployment.png',
        alt: 'Deployment summary',
      }
    case 'table':
      return {
        id: draftId(),
        type,
        columns: ['Service', 'Status'],
        rows: [
          ['API', 'Healthy'],
          ['Web', 'Healthy'],
        ],
      }
    case 'actions':
      return {
        id: draftId(),
        type,
        buttons: [{ text: 'Open deployment', url: 'https://example.com/deployments/412' }],
      }
  }
}

function toBlock(draft: BlockDraft): WebhookBlock {
  switch (draft.type) {
    case 'title':
    case 'text':
      return { type: draft.type, text: draft.text }
    case 'image':
      return { type: 'image', url: draft.url, alt: draft.alt }
    case 'table':
      return { type: 'table', columns: draft.columns, rows: draft.rows }
    case 'actions':
      return { type: 'actions', buttons: draft.buttons }
  }
}

function resizeTable(draft: TableDraft, width: number): TableDraft {
  const columns = Array.from({ length: width }, (_, index) => draft.columns[index] ?? `Column ${index + 1}`)
  const rows = draft.rows.map((row) =>
    Array.from({ length: width }, (_, index) => row[index] ?? ''),
  )
  return { ...draft, columns, rows }
}

function shellQuote(value: string): string {
  return `'${value.replaceAll("'", `'\\''`)}'`
}

async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard !== undefined) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    // The textarea fallback works on non-secure LAN origins.
  }

  try {
    const area = document.createElement('textarea')
    area.value = value
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.top = '-1000px'
    document.body.appendChild(area)
    area.select()
    const copied = document.execCommand('copy')
    document.body.removeChild(area)
    return copied
  } catch {
    return false
  }
}

interface BlockEditorProps {
  draft: BlockDraft
  index: number
  total: number
  onChange: (next: BlockDraft) => void
  onRemove: () => void
  onMove: (direction: -1 | 1) => void
}

function BlockEditor({
  draft,
  index,
  total,
  onChange,
  onRemove,
  onMove,
}: BlockEditorProps): ReactElement {
  return (
    <li className={styles.blockCard}>
      <div className={styles.blockHeader}>
        <span className={styles.blockName}>{draft.type}</span>
        <div className={styles.blockControls}>
          <Button size="sm" variant="ghost" disabled={index === 0} onClick={() => onMove(-1)}>
            Up
          </Button>
          <Button size="sm" variant="ghost" disabled={index === total - 1} onClick={() => onMove(1)}>
            Down
          </Button>
          <Button size="sm" variant="ghost" onClick={onRemove}>
            Remove
          </Button>
        </div>
      </div>

      {draft.type === 'title' ? (
        <TextField
          label="Title"
          value={draft.text}
          maxLength={200}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange({ ...draft, text: event.target.value })
          }
        />
      ) : draft.type === 'text' ? (
        <TextArea
          label="Text"
          hint="Markdown is supported. HTML is always treated as text."
          value={draft.text}
          maxLength={16_000}
          rows={4}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
            onChange({ ...draft, text: event.target.value })
          }
        />
      ) : draft.type === 'image' ? (
        <div className={styles.fieldGrid}>
          <TextField
            label="Image URL"
            type="url"
            value={draft.url}
            maxLength={2_048}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onChange({ ...draft, url: event.target.value })
            }
          />
          <TextField
            label="Alternative text"
            hint="Describe the image for people who cannot see it."
            value={draft.alt}
            maxLength={300}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onChange({ ...draft, alt: event.target.value })
            }
          />
        </div>
      ) : draft.type === 'table' ? (
        <TableEditor draft={draft} onChange={onChange} />
      ) : (
        <ActionsEditor draft={draft} onChange={onChange} />
      )}
    </li>
  )
}

function TableEditor({
  draft,
  onChange,
}: {
  draft: TableDraft
  onChange: (next: BlockDraft) => void
}): ReactElement {
  return (
    <div className={styles.tableEditor}>
      <Select
        label="Columns"
        hint="A table can have up to four columns."
        value={String(draft.columns.length)}
        options={[1, 2, 3, 4].map((count) => ({ value: String(count), label: String(count) }))}
        onChange={(event: ChangeEvent<HTMLSelectElement>) =>
          onChange(resizeTable(draft, Number.parseInt(event.target.value, 10)))
        }
      />

      <div className={styles.tableFields}>
        <div className={styles.tableFieldRow}>
          {draft.columns.map((column, columnIndex) => (
            <TextField
              label={`Column ${columnIndex + 1}`}
              value={column}
              maxLength={120}
              key={`column-${columnIndex}`}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const columns = [...draft.columns]
                columns[columnIndex] = event.target.value
                onChange({ ...draft, columns })
              }}
            />
          ))}
        </div>

        {draft.rows.map((row, rowIndex) => (
          <div className={styles.tableRowWrap} key={`row-${rowIndex}`}>
            <div className={styles.tableFieldRow}>
              {row.map((cell, columnIndex) => (
                <TextField
                  label={`Row ${rowIndex + 1}, ${draft.columns[columnIndex] || `column ${columnIndex + 1}`}`}
                  labelHidden
                  value={cell}
                  maxLength={500}
                  key={`cell-${rowIndex}-${columnIndex}`}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const rows = draft.rows.map((existing) => [...existing])
                    const target = rows[rowIndex]
                    if (target !== undefined) target[columnIndex] = event.target.value
                    onChange({ ...draft, rows })
                  }}
                />
              ))}
            </div>
            <Button
              size="sm"
              variant="ghost"
              disabled={draft.rows.length === 1}
              onClick={() =>
                onChange({ ...draft, rows: draft.rows.filter((_, current) => current !== rowIndex) })
              }
            >
              Remove row
            </Button>
          </div>
        ))}
      </div>

      <Button
        size="sm"
        disabled={draft.rows.length >= TABLE_ROW_LIMIT}
        onClick={() =>
          onChange({ ...draft, rows: [...draft.rows, draft.columns.map(() => '')] })
        }
      >
        Add row
      </Button>
    </div>
  )
}

function ActionsEditor({
  draft,
  onChange,
}: {
  draft: ActionsDraft
  onChange: (next: BlockDraft) => void
}): ReactElement {
  return (
    <div className={styles.actionEditor}>
      {draft.buttons.map((button, index) => (
        <div className={styles.actionRow} key={`button-${index}`}>
          <TextField
            label={`Button ${index + 1} label`}
            value={button.text}
            maxLength={80}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const buttons = draft.buttons.map((existing) => ({ ...existing }))
              const target = buttons[index]
              if (target !== undefined) target.text = event.target.value
              onChange({ ...draft, buttons })
            }}
          />
          <TextField
            label={`Button ${index + 1} URL`}
            type="url"
            value={button.url}
            maxLength={2_048}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const buttons = draft.buttons.map((existing) => ({ ...existing }))
              const target = buttons[index]
              if (target !== undefined) target.url = event.target.value
              onChange({ ...draft, buttons })
            }}
          />
          <Button
            size="sm"
            variant="ghost"
            disabled={draft.buttons.length === 1}
            onClick={() =>
              onChange({
                ...draft,
                buttons: draft.buttons.filter((_, current) => current !== index),
              })
            }
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        size="sm"
        disabled={draft.buttons.length >= 4}
        onClick={() =>
          onChange({
            ...draft,
            buttons: [...draft.buttons, { text: 'Open', url: 'https://example.com' }],
          })
        }
      >
        Add button
      </Button>
      <p className={styles.limitHint}>Up to four buttons are shown on one row.</p>
    </div>
  )
}

export interface WebhookMessageBuilderProps {
  webhookUrl: string
}

export function WebhookMessageBuilder({ webhookUrl }: WebhookMessageBuilderProps): ReactElement {
  const toast = useToast()
  const [mode, setMode] = useState<BuilderMode>('basic')
  const [name, setName] = useState('CI')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [basicText, setBasicText] = useState('Build 412 passed all checks.')
  const [drafts, setDrafts] = useState<BlockDraft[]>([newDraft('title'), newDraft('text')])
  const [copied, setCopied] = useState<'json' | 'curl' | null>(null)

  const blocks = useMemo(() => drafts.map(toBlock), [drafts])
  const payload = useMemo<WebhookIngressRequest>(() => {
    const identity = {
      ...(name.trim() === '' ? {} : { name: name.trim() }),
      ...(avatarUrl.trim() === '' ? {} : { avatar_url: avatarUrl.trim() }),
    }

    return mode === 'basic'
      ? { format: 'basic', text: basicText, ...identity }
      : { format: 'blocks', blocks, ...identity }
  }, [avatarUrl, basicText, blocks, mode, name])

  const json = useMemo(() => JSON.stringify(payload, null, 2), [payload])
  const curl = useMemo(
    () =>
      `curl --request POST ${shellQuote(webhookUrl)} \\\n  --header 'Content-Type: application/json' \\\n  --data ${shellQuote(JSON.stringify(payload))}`,
    [payload, webhookUrl],
  )

  const updateDraft = (id: string, next: BlockDraft): void => {
    setDrafts((current) => current.map((draft) => (draft.id === id ? next : draft)))
  }

  const moveDraft = (index: number, direction: -1 | 1): void => {
    setDrafts((current) => {
      const target = index + direction
      if (target < 0 || target >= current.length) return current
      const next = [...current]
      const [moved] = next.splice(index, 1)
      if (moved === undefined) return current
      next.splice(target, 0, moved)
      return next
    })
  }

  const copy = (kind: 'json' | 'curl', value: string): void => {
    void copyText(value).then((ok) => {
      if (!ok) {
        toast.error('The browser would not let the page copy this request.')
        return
      }
      setCopied(kind)
      window.setTimeout(() => setCopied(null), 2_000)
    })
  }

  const previewName = name.trim() === '' ? 'Webhook' : name.trim()

  return (
    <div className={styles.builder}>
      <div className={styles.modePicker} role="group" aria-label="Message format">
        <Button variant={mode === 'basic' ? 'primary' : 'secondary'} onClick={() => setMode('basic')}>
          Basic
        </Button>
        <Button variant={mode === 'blocks' ? 'primary' : 'secondary'} onClick={() => setMode('blocks')}>
          Blocks
        </Button>
      </div>

      <div className={styles.workspace}>
        <div className={styles.editor}>
          <div className={styles.identityFields}>
            <TextField
              label="Custom name"
              hint="Optional. Applies to this message only."
              value={name}
              maxLength={80}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
            />
            <TextField
              label="Custom avatar URL"
              hint="Optional HTTP(S) image link."
              type="url"
              value={avatarUrl}
              maxLength={2_048}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setAvatarUrl(event.target.value)}
            />
          </div>

          {mode === 'basic' ? (
            <TextArea
              label="Message text"
              hint="Markdown is supported."
              value={basicText}
              maxLength={16_000}
              rows={5}
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setBasicText(event.target.value)}
            />
          ) : (
            <>
              <ul className={styles.blockList}>
                {drafts.map((draft, index) => (
                  <BlockEditor
                    key={draft.id}
                    draft={draft}
                    index={index}
                    total={drafts.length}
                    onChange={(next) => updateDraft(draft.id, next)}
                    onRemove={() => setDrafts((current) => current.filter((item) => item.id !== draft.id))}
                    onMove={(direction) => moveDraft(index, direction)}
                  />
                ))}
              </ul>

              <div className={styles.addBlocks} aria-label="Add a block">
                <span className={styles.addLabel}>Add block</span>
                {(['title', 'text', 'image', 'table', 'actions'] as const).map((type) => (
                  <Button
                    size="sm"
                    key={type}
                    disabled={drafts.length >= BLOCK_LIMIT}
                    onClick={() => setDrafts((current) => [...current, newDraft(type)])}
                  >
                    {type}
                  </Button>
                ))}
              </div>
            </>
          )}
        </div>

        <aside className={styles.preview} aria-label="Message preview">
          <span className={styles.previewLabel}>Preview</span>
          <div className={styles.previewMessage}>
            <div className={styles.previewHeader}>
              <Avatar name={previewName} id={previewName} src={avatarUrl.trim() || null} size="sm" decorative />
              <span>{previewName}</span>
              <span className={styles.previewBadge}>webhook</span>
            </div>
            {mode === 'basic' ? (
              <p className={styles.basicPreview}>{basicText || 'Your message will appear here.'}</p>
            ) : blocks.length === 0 ? (
              <p className={styles.emptyPreview}>Add a block to preview the message.</p>
            ) : (
              <WebhookBlocks blocks={blocks} keyPrefix="webhook-preview" />
            )}
          </div>
        </aside>
      </div>

      <div className={styles.outputGrid}>
        <div className={styles.output}>
          <div className={styles.outputHeader}>
            <span>JSON payload</span>
            <Button size="sm" onClick={() => copy('json', json)}>
              {copied === 'json' ? 'Copied' : 'Copy JSON'}
            </Button>
          </div>
          <code>{json}</code>
        </div>
        <div className={styles.output}>
          <div className={styles.outputHeader}>
            <span>cURL request</span>
            <Button size="sm" onClick={() => copy('curl', curl)}>
              {copied === 'curl' ? 'Copied' : 'Copy cURL'}
            </Button>
          </div>
          <code>{curl}</code>
        </div>
      </div>
    </div>
  )
}

export default WebhookMessageBuilder
